// Types for facility data from backend API

export interface FacilityCoordinate {
  id: string;
  latitude: number;
  longitude: number;
  facility_type: string[];
  name: string;
}

export interface MapCoordinatesResponse {
  coordinates: FacilityCoordinate[];
  count: number;
}

// Transformed facility data for frontend use
export interface FacilityData {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  coordinates: [number, number]; // [lat, lng] for Leaflet
  facility_type: string[];
  type: string; // Primary type for display
  region: string; // Will be derived from coordinates or set as default
  address: string; // Will be derived or set as default
  description: string; // Will be generated from available data
  industry: string; // Will be mapped from facility_type
  facilityStatus: string; // Default status
}

// Utility function to transform backend data to frontend format
export const transformFacilityData = (
  backendFacility: FacilityCoordinate
): FacilityData => {
  const primaryType = backendFacility.facility_type[0] || "facility";

  return {
    id: backendFacility.id,
    name: backendFacility.name,
    latitude: backendFacility.latitude,
    longitude: backendFacility.longitude,
    coordinates: [backendFacility.latitude, backendFacility.longitude],
    facility_type: backendFacility.facility_type,
    type: primaryType,
    region: getRegionFromCoordinates(
      backendFacility.latitude,
      backendFacility.longitude
    ),
    address: `${backendFacility.latitude.toFixed(4)}, ${backendFacility.longitude.toFixed(4)}`,
    description: `${backendFacility.name} is a ${primaryType} facility with ${backendFacility.facility_type.length} facility types.`,
    industry: mapFacilityTypeToIndustry(primaryType),
    facilityStatus: "operational", // Default status
  };
};

// Helper function to determine region from coordinates
const getRegionFromCoordinates = (lat: number, lng: number): string => {
  // Simple region mapping based on coordinates
  if (lat >= 35 && lat <= 70 && lng >= -10 && lng <= 40) return "Europe";
  if (lat >= 25 && lat <= 50 && lng >= -125 && lng <= -65)
    return "North America";
  if (lat >= -35 && lat <= -10 && lng >= 110 && lng <= 155) return "Australia";
  if (lat >= 35 && lat <= 55 && lng >= 25 && lng <= 70) return "Middle East";
  if (lat >= -35 && lat <= 35 && lng >= -20 && lng <= 55) return "Africa";
  if (lat >= -15 && lat <= 55 && lng >= 60 && lng <= 150) return "Asia";
  if (lat >= -60 && lat <= 15 && lng >= -85 && lng <= -35)
    return "South America";
  return "Other";
};

// Detailed facility data from backend API
export interface DetailedFacility {
  f: {
    id: string;
    name: string;
    facility_type: string[];
    labels: string[];
    description: string;
    status_labels: string[];
    status_summary: string;
    status_last_update_date: string;
    phases: {
      status_event_id: string;
      status_label: string;
      status_summary: string;
      status_date: string;
      facility_operational_date: string;
      published_date: string;
      reference_event_id: string;
      source_ids: string;
    }[];
    operational_from: string;
    number_of_employees: number;
    services: string[];
    production_kpis: {
      capacity_id: string;
      capacity_value: number;
      capacity_unit: string;
      product_or_service: string;
      reference_business_unit: string;
      reference_period: string;
      reference_frequency: string;
      reference_sub_facility_id: string;
      summary: string;
      last_updated_date: string;
    }[];
    technologies_used: string[];
    products_produced: string[];
    product_use_cases: string[];
    naics_codes_with_title: string[];
    input_hs_codes_with_title: string[];
    output_hs_codes_with_title: string[];
    confidence_level: number;
    n_sources: number;
    country_iso2: string;
    address: string;
    latitude: number;
    longitude: number;
    precision_level: string;
  };
  organizations: {
    organization: any;
    relationship: {
      relation_type?: string;
      semantic_role?: string;
      [key: string]: any;
    };
  }[];
  projects: {
    project: any;
    relationship: {
      relation_type?: string;
      semantic_role?: string;
      project_role?: string;
      [key: string]: any;
    };
  }[];
  sources: {
    source: any;
    relationship: {
      relation_type?: string;
      semantic_role?: string;
      [key: string]: any;
    };
  }[];
  facilities: {
    facility: any;
    relationship: {
      relation_type?: string;
      semantic_role?: string;
      [key: string]: any;
    };
  }[];
}

// Helper function to map facility types to industries
const mapFacilityTypeToIndustry = (facilityType: string): string => {
  const industryMap: { [key: string]: string } = {
    factory: "Manufacturing",
    research_and_development: "Technology",
    office: "Business Services",
    airport: "Transportation",
    infrastructure: "Infrastructure",
    service_center: "Services",
    exhibition_booth: "Events & Exhibitions",
    hangar: "Aviation",
  };

  return industryMap[facilityType] || "Other";
};
